import { type ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { useChatInput } from '@/components/ChatInput/useChatInput';
import {
  REPLICA_WARMUP_LABEL,
  StartupStatusBanner,
  VM_BOOT_LABEL,
} from '@/components/ChatInput/StartupStatusBanner';
import { useAppSession } from '@/contexts/SessionContext';
import { useVmConnection } from '@/api/context';
import { useDevBannerOverrides } from '@/lib/devBannerTriggers';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Titles, Urls } from '@/lib/navigation';
import { websiteUrls } from '@/lib/site-urls';
import { trackDemosClicked } from '@/analytics';
import agenticaLogo from '@/assets/agenticabeta.svg';
import agenticaWordmark from '@/assets/agentica.svg';
import { Logomark } from './Logomark';
import { PaperChatInput } from './PaperChatInput';
import { PaperMobileTabBar } from './PaperMobileTabBar';
import {
  PAPER_CONTROL_RADIUS_CLASS,
  PAPER_ICON_SIZE,
  PAPER_ICON_STROKE_WIDTH,
  PAPER_ICON_TINY_CLASS,
} from './paperShapes';

const PaperDemosLink = () => (
  <div className="mt-[14px] flex justify-center">
    <Link
      to={Urls.demos()}
      onClick={trackDemosClicked}
      className={cn(
        'inline-flex items-center gap-[8px] h-[34px] px-[16px]',
        PAPER_CONTROL_RADIUS_CLASS,
        'bg-[var(--surface)] text-[var(--fg-2)]',
        'border border-solid border-[var(--hair)]',
        'text-[12px] font-medium tracking-[0.02em] no-underline',
        'transition-colors duration-200',
        'hover:text-[var(--fg)] hover:border-[var(--hair-strong)] hover:bg-[var(--bg-alt)]',
      )}
    >
      <Sparkles className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
      See demos
      <ArrowRight className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
    </Link>
  </div>
);

const PaperLandingLogo = ({ className }: { className?: string }) => (
  <div className={cn('flex justify-center', className)}>
    <Link to={Urls.demos()} className="inline-flex no-underline">
      <img
        src={agenticaLogo}
        alt="Agentica"
        className="paper-landing-logo block h-[48px] w-auto transition-opacity duration-200 hover:opacity-80"
      />
    </Link>
  </div>
);

interface PaperLandingHeaderProps {
  onSignIn?: () => void;
  reserved?: boolean;
}

const PaperLandingHeader = (props: PaperLandingHeaderProps) => (
  <header
    aria-hidden={props.reserved || undefined}
    className={cn(
      'relative z-10 flex items-center justify-between shrink-0 px-[24px] py-[18px]',
      props.reserved && 'invisible pointer-events-none',
    )}
  >
    <a
      href="/"
      tabIndex={props.reserved ? -1 : undefined}
      className={cn(
        'inline-flex items-center gap-[10px]',
        'text-[var(--fg)] no-underline',
      )}
    >
      <Logomark size={PAPER_ICON_SIZE} />
      <span
        className="text-[16px] tracking-[-0.01em]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        Symbolica
      </span>
      <span
        className={cn(
          'ml-[2px] pl-[12px] border-l border-solid border-[var(--hair)]',
          'inline-flex items-center',
        )}
      >
        <img
          src={agenticaWordmark}
          alt="Agentica"
          className="paper-landing-logo block h-[10px] w-auto"
        />
      </span>
    </a>
    <button
      type="button"
      onClick={props.onSignIn}
      tabIndex={props.reserved ? -1 : undefined}
      className={cn(
        'inline-flex items-center gap-[6px] h-[34px] px-[14px]',
        PAPER_CONTROL_RADIUS_CLASS,
        'bg-[var(--fg)] text-[var(--fg-inverse)] border border-solid border-[var(--fg)]',
        'text-[12px] font-medium tracking-[0.02em] cursor-pointer',
        'transition-colors duration-200',
        'hover:bg-[var(--ink-3)] hover:border-[var(--ink-3)]',
      )}
    >
      Sign in
      <ArrowRight className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
    </button>
  </header>
);

interface PaperLandingShellProps {
  header: ReactNode;
  footer: ReactNode;
  children: ReactNode;
  background?: ReactNode;
  /** Rendered at the very bottom (below the footer). Used for the mobile tab
   *  bar on the authed landing; footer hides on mobile when this is provided. */
  bottomBar?: ReactNode;
}

const PaperLandingShell = (props: PaperLandingShellProps) => (
  <div
    className="fixed inset-0 z-0 flex flex-col overflow-hidden bg-[var(--bg)] text-[var(--fg)]"
    style={{ fontFamily: 'var(--font-sans-paper)' }}
  >
    {props.background}
    {props.header}
    <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-[20px] py-[24px] desktop:px-[28px] desktop:py-[40px]">
      {props.children}
    </main>
    {props.bottomBar ? (
      <>
        <div className="hidden desktop:contents">{props.footer}</div>
        {props.bottomBar}
      </>
    ) : (
      props.footer
    )}
  </div>
);

interface PaperLandingCenterProps {
  chatInput: ReturnType<typeof useChatInput>;
  placeholder: string;
  showVmBoot?: boolean;
}

const PaperLandingCenter = (props: PaperLandingCenterProps) => {
  const isMobileDevice = useIsMobileDevice();

  return (
    <>
      <PaperLandingLogo className="mb-[28px]" />
      {props.showVmBoot && (
        <div className="w-full max-w-[640px] mb-[10px]">
          <StartupStatusBanner status={VM_BOOT_LABEL} />
        </div>
      )}
      <div className="w-full max-w-[640px]">
        <PaperChatInput
          {...props.chatInput}
          isRunning={false}
          placeholder={props.placeholder}
          autoFocus={!isMobileDevice}
          className="!px-0 !pb-0"
        />
        <PaperDemosLink />
      </div>
    </>
  );
};

export const PaperLandingPage = () => {
  useDocumentTitle(Titles.chat());
  const auth = useAuth();

  if (!auth.isAuthenticated) return <PaperLandingAnon onSignIn={auth.signIn} />;
  return <PaperLandingAuthed />;
};

const useVmBootVisible = () => {
  const vmConn = useVmConnection();
  const override = useDevBannerOverrides().vmBoot;
  const [delayed, setDelayed] = useState(false);
  useEffect(() => {
    if (vmConn.status !== 'connecting') {
      setDelayed(false);
      return;
    }
    const t = setTimeout(() => setDelayed(true), 1000);
    return () => clearTimeout(t);
  }, [vmConn.status]);
  return override || delayed;
};

const PaperLegalLinks = () => (
  <span className="flex items-center gap-[14px]">
    <a
      href={websiteUrls.termsOfUse}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[var(--fg-4)] hover:text-[var(--fg-2)]"
    >
      Terms
    </a>
    <a
      href={websiteUrls.privacyPolicy}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[var(--fg-4)] hover:text-[var(--fg-2)]"
    >
      Privacy
    </a>
  </span>
);

const PaperLandingFooter = () => (
  <footer
    className={cn(
      'relative z-10 flex items-center justify-between shrink-0',
      'text-[11px] text-[var(--fg-4)]',
    )}
    style={{ padding: '16px 24px' }}
  >
    <span />
    <PaperLegalLinks />
  </footer>
);

const PaperLandingAuthed = () => {
  const { session } = useAppSession();
  const chatInput = useChatInput({
    isRunning: false,
    startupLabel: REPLICA_WARMUP_LABEL,
    sendInFlight: session.chat.sendInFlight,
  });
  const showVmBoot = useVmBootVisible();
  return (
    <PaperLandingShell
      header={<PaperLandingHeader reserved />}
      footer={<PaperLandingFooter />}
      bottomBar={<PaperMobileTabBar className="desktop:hidden" active="chat" />}
    >
      <PaperLandingCenter
        chatInput={chatInput}
        placeholder="What would you like to build?"
        showVmBoot={showVmBoot}
      />
    </PaperLandingShell>
  );
};

interface PaperLandingAnonProps {
  onSignIn: () => void;
}

const PaperLandingAnon = (props: PaperLandingAnonProps) => {
  const { session } = useAppSession();
  const chatInput = useChatInput({
    isRunning: false,
    startupLabel: REPLICA_WARMUP_LABEL,
    sendInFlight: session.chat.sendInFlight,
  });

  return (
    <PaperLandingShell
      background={<div className="ag-hero-bg" />}
      header={<PaperLandingHeader onSignIn={props.onSignIn} />}
      footer={<PaperLandingFooter />}
    >
      <PaperLandingCenter
        chatInput={chatInput}
        placeholder="Ask Agentica to build, research, monitor…"
      />
    </PaperLandingShell>
  );
};
